// Configurações do curso: nome, comando, tema, intervalo, aprovação e lembrete.
import React, { useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FiAlertTriangle } from 'react-icons/fi';
import { firestore } from '../../config/firebaseConfig';
import { corteDeAprovacao } from './mensagensBot';
import {
  ATALHOS_INTERVALO, LIMITES, configDoCurso, formatarIntervalo, normalizarComando, plural,
  temaDoCurso, validarComando, validarInteiro,
} from './util';

function formDoCurso(curso, temas) {
  const cfg = configDoCurso(curso);
  const tema = temaDoCurso(curso, temas);
  return {
    nome: curso.nome || '',
    comando: curso.comando || '',
    tema: tema ? tema.id : String(curso.tema || ''),
    intervalo: String(cfg.intervalo),
    aprovacao: String(cfg.aprovacao),
    lembrete: String(cfg.lembrete),
  };
}

const iguais = (a, b) => Object.keys(a).every((k) => a[k] === b[k]);

function ConfigCurso({ curso, cursos, temas, nQuiz, andamento, onSujo }) {
  const original = useMemo(() => formDoCurso(curso, temas), [curso, temas]);
  const [form, setForm] = useState(original);
  const [salvando, setSalvando] = useState(false);
  const sujo = !iguais(form, original);

  // outro curso aberto ou alteração vinda de fora sem nada pendente aqui: acompanha o banco
  useEffect(() => { setForm(original); }, [curso.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!sujo) setForm(original); }, [original]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onSujo(sujo); }, [sujo, onSujo]);

  const erros = {
    nome: form.nome.trim() ? '' : 'Informe o nome do curso.',
    comando: validarComando(form.comando, cursos, curso.id),
    intervalo: validarInteiro(form.intervalo, LIMITES.intervalo, 'o intervalo'),
    aprovacao: validarInteiro(form.aprovacao, LIMITES.aprovacao, 'o percentual'),
    lembrete: validarInteiro(form.lembrete, LIMITES.lembrete, 'as horas'),
  };
  const valido = Object.values(erros).every((e) => !e);
  const comandoMudou = form.comando !== original.comando;

  const alterar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const salvar = async (e) => {
    e.preventDefault();
    if (!valido || !sujo) return;
    const mudancas = {};
    if (form.nome.trim() !== (curso.nome || '')) mudancas.nome = form.nome.trim();
    if (form.comando !== original.comando) mudancas.comando = form.comando;
    if (form.tema !== original.tema) mudancas.tema = form.tema;
    if (form.intervalo !== original.intervalo) mudancas.intervaloEtapasEmMinutos = Number(form.intervalo);
    if (form.aprovacao !== original.aprovacao) mudancas.percentualAprovacao = Number(form.aprovacao);
    if (form.lembrete !== original.lembrete) mudancas.lembreteAposHoras = Number(form.lembrete);
    if (!Object.keys(mudancas).length) { setForm(original); return; }
    setSalvando(true);
    try {
      await updateDoc(doc(firestore, 'minicursos', curso.id), mudancas);
      toast.success('Configurações salvas. O bot já usa os novos valores.');
    } catch (erro) {
      console.error('Erro ao salvar o minicurso:', erro);
      toast.error('Não foi possível salvar as configurações.');
    } finally {
      setSalvando(false);
    }
  };

  const ajuda = (id, texto, erro) => (
    <span id={id} className={erro ? 'mc-campo__erro' : 'mc-campo__ajuda'}>{erro || texto}</span>
  );

  const intervaloN = Number(form.intervalo);
  const aprovacaoN = Number(form.aprovacao);
  const lembreteN = Number(form.lembrete);
  const temaInexistente = form.tema && !temas.some((t) => t.id === form.tema);

  return (
    <form className="mc-form mc-config" onSubmit={salvar} noValidate>
      <div className="mc-grade">
        <div className="mc-campo mc-campo--largo">
          <label htmlFor="mc-cfg-nome">Nome do curso</label>
          <input
            id="mc-cfg-nome"
            type="text"
            value={form.nome}
            maxLength={LIMITES.nome}
            onChange={alterar('nome')}
            aria-invalid={Boolean(erros.nome)}
            aria-describedby="mc-cfg-nome-ajuda"
          />
          {ajuda('mc-cfg-nome-ajuda', 'Aparece no menu Cursos do WhatsApp e nas mensagens do bot.', erros.nome)}
        </div>

        <div className="mc-campo mc-campo--largo">
          <label htmlFor="mc-cfg-comando">Comando no WhatsApp</label>
          <input
            id="mc-cfg-comando"
            type="text"
            className="mc-mono"
            value={form.comando}
            onChange={(e) => setForm((f) => ({ ...f, comando: normalizarComando(e.target.value) }))}
            aria-invalid={Boolean(erros.comando)}
            aria-describedby="mc-cfg-comando-ajuda mc-cfg-comando-aviso"
            autoComplete="off"
            spellCheck={false}
          />
          {ajuda('mc-cfg-comando-ajuda', 'O aluno envia exatamente este texto para começar. Único na empresa: INICIAR_ + letras, números ou _.', erros.comando)}
          {comandoMudou && original.comando && (
            <p id="mc-cfg-comando-aviso" className="mc-alerta mc-alerta--atencao">
              <FiAlertTriangle aria-hidden="true" />
              <span>
                Ao salvar, <code className="mc-comando">{original.comando}</code> deixa de funcionar: convites, cartazes e
                mensagens que já divulgaram esse comando não vão mais iniciar o curso.
                {andamento > 0 && ` Os ${plural(andamento, 'aluno', 'alunos')} em andamento não são afetados.`}
              </span>
            </p>
          )}
        </div>

        <div className="mc-campo mc-campo--largo">
          <label htmlFor="mc-cfg-tema">Tema</label>
          <select id="mc-cfg-tema" value={form.tema} onChange={alterar('tema')} aria-describedby="mc-cfg-tema-ajuda">
            <option value="">Sem tema</option>
            {temaInexistente && <option value={form.tema}>{form.tema} (tema não encontrado)</option>}
            {temas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
          {ajuda('mc-cfg-tema-ajuda', 'Usado para sugerir pílulas e questões ao adicionar etapas. Não muda nada para o aluno.')}
        </div>

        <div className="mc-campo mc-campo--largo">
          <label htmlFor="mc-cfg-intervalo">Intervalo entre etapas</label>
          <div className="mc-intervalo">
            <div className="mc-com-sufixo">
              <input
                id="mc-cfg-intervalo"
                type="number"
                inputMode="numeric"
                min={LIMITES.intervalo.min}
                max={LIMITES.intervalo.max}
                step="1"
                value={form.intervalo}
                onChange={alterar('intervalo')}
                aria-invalid={Boolean(erros.intervalo)}
                aria-describedby="mc-cfg-intervalo-ajuda"
              />
              <span className="mc-com-sufixo__sufixo" aria-hidden="true">min</span>
            </div>
            <div className="mc-atalhos" role="group" aria-label="Atalhos de intervalo">
              {ATALHOS_INTERVALO.map((a) => (
                <button
                  key={a.minutos}
                  type="button"
                  className={`mc-filtro${intervaloN === a.minutos ? ' is-ativo' : ''}`}
                  aria-pressed={intervaloN === a.minutos}
                  onClick={() => setForm((f) => ({ ...f, intervalo: String(a.minutos) }))}
                >
                  {a.rotulo}
                </button>
              ))}
            </div>
          </div>
          {ajuda(
            'mc-cfg-intervalo-ajuda',
            `${erros.intervalo ? '' : `= ${formatarIntervalo(intervaloN)}. `}Tempo até a próxima etapa; depois de uma questão, conta a partir da resposta.`,
            erros.intervalo,
          )}
        </div>

        <div className="mc-campo">
          <label htmlFor="mc-cfg-aprovacao">Aprovação</label>
          <div className="mc-com-sufixo">
            <input
              id="mc-cfg-aprovacao"
              type="number"
              inputMode="numeric"
              min={LIMITES.aprovacao.min}
              max={LIMITES.aprovacao.max}
              step="1"
              value={form.aprovacao}
              onChange={alterar('aprovacao')}
              aria-invalid={Boolean(erros.aprovacao)}
              aria-describedby="mc-cfg-aprovacao-ajuda"
            />
            <span className="mc-com-sufixo__sufixo" aria-hidden="true">% de acertos</span>
          </div>
          {ajuda(
            'mc-cfg-aprovacao-ajuda',
            nQuiz > 0
              ? `Com ${plural(nQuiz, 'questão', 'questões')} de quiz, aprova quem acertar pelo menos ${corteDeAprovacao(aprovacaoN, nQuiz)}.`
              : 'Só as questões de quiz contam para a nota.',
            erros.aprovacao,
          )}
        </div>

        <div className="mc-campo">
          <label htmlFor="mc-cfg-lembrete">Lembrete sem resposta</label>
          <div className="mc-com-sufixo">
            <input
              id="mc-cfg-lembrete"
              type="number"
              inputMode="numeric"
              min={LIMITES.lembrete.min}
              max={LIMITES.lembrete.max}
              step="1"
              value={form.lembrete}
              onChange={alterar('lembrete')}
              aria-invalid={Boolean(erros.lembrete)}
              aria-describedby="mc-cfg-lembrete-ajuda"
            />
            <span className="mc-com-sufixo__sufixo" aria-hidden="true">horas</span>
          </div>
          {ajuda(
            'mc-cfg-lembrete-ajuda',
            lembreteN === 0
              ? 'Desligado: o bot não lembra quem não respondeu.'
              : `Se o aluno não responder uma questão em ${plural(lembreteN, 'hora', 'horas')}, o bot manda um lembrete. Use 0 para desligar.`,
            erros.lembrete,
          )}
        </div>
      </div>

      <footer className="mc-config__rodape">
        {sujo && <span className="mc-suave" role="status">Alterações não salvas</span>}
        <button type="button" className="btn-secondary mc-botao" onClick={() => setForm(original)} disabled={!sujo || salvando}>
          Descartar
        </button>
        <button type="submit" className="btn-primary mc-botao" disabled={!sujo || !valido || salvando}>
          {salvando ? 'Salvando...' : 'Salvar configurações'}
        </button>
      </footer>
    </form>
  );
}

export default ConfigCurso;
